import type { ProductCardExtended } from '#root/api/clients/product/data-contracts'
import type { Responsive } from '#types/common'
import type { FocusedProductContent } from '#types/components/cms/focused-product'
import type { FocusedProductPlacement } from '#types/components/product/list'
import ds from '#root/design-system'

/** A focused product spans this many columns and rows. */
export const FOCUSED_PRODUCT_SPAN = 2

const sum = (total: number, value?: number) => total + (value || 0)

/**
 * Whether a product card is the one a `focused-product-espot` eSpot refers to.
 *
 * CMS authors reference a product by style id (`NF0A88ZX`) while the catalog keys cards by
 * style-color id (`NF0A88ZXLE4`), so an equality check alone would never match.
 * @param product - A product from the current product list.
 * @param productId - The `productId` authored on the eSpot.
 * @returns `true` when the card represents that product.
 * @category Utils
 */
export const matchesFocusedProduct = (product: ProductCardExtended, productId: string) =>
  product.id === productId
  || (PRODUCT_STYLE_ID_REGEX.test(productId) && product.id.startsWith(productId))

/**
 * Resolves `focused-product-espot` eSpots into explicit grid placements for the product cards they match.
 *
 * `row` is relative to the batch ("load more" page) the matched product belongs to, not to the top
 * of the grid - a product that only arrives with the second batch takes `row` of that batch, so
 * loading more never moves what is already on screen.
 *
 * A product that is not in the list, sits past the end of the grid, or is hidden at a breakpoint
 * simply flows as a normal card there (`'auto'`) - unlike an eSpot, it must never be display:none.
 *
 * An item with `poweredBySpotlightParameter` takes its product from the `?spotlight=` query
 * parameter instead of its authored `productId`, so the same eSpot can feature a different product
 * per link. Such an item features nothing when the parameter is absent - the author opted the slot
 * into being URL-driven, so silently falling back to the authored id would surprise them.
 *
 * An `assetOverride` is resolved per breakpoint and leads the card's gallery, ahead of the product's
 * own media rather than in place of it. It is not tied to the breakpoints the tile is featured at,
 * so a card carries the same media wherever it lands in the grid.
 * @param items - The `focused-product-espot` eSpot items from CMS.
 * @param products - Products currently rendered in the grid, in grid order.
 * @param cols - Responsive number of grid columns per breakpoint.
 * @param productsPerPage - Batch size, used to derive which page a matched product came from.
 * @param spotlightProductId - Value of the `?spotlight=` query parameter, if present.
 * @param isLegacyFocused - Transition-only: identifies catalog-driven focused cards, which take
 * two cells at `sm`. Remove together with the legacy focused-product path.
 * @returns Placements keyed by the matched product's index in `products`; matches that are featured
 * at no breakpoint are omitted. Indexes rather than ids, so a duplicated product id cannot promote
 * more than the one card the eSpot resolved to. Each placement carries `focused` per breakpoint,
 * which the card uses to size its media for the 2x2 area rather than a single cell.
 * @category Utils
 */
export const resolveFocusedProducts = (
  items: FocusedProductContent[],
  products: ProductCardExtended[],
  cols: Responsive<number>,
  productsPerPage: number,
  spotlightProductId?: string,
  isLegacyFocused: (product: ProductCardExtended) => boolean = () => false
): Map<number, FocusedProductPlacement> => {
  const seen = new Set<number>()
  const matches = items
    .map((item) => {
      const productId = item.poweredBySpotlightParameter ? spotlightProductId : item.productId
      return {
        item,
        index: productId
          ? products.findIndex((product) => matchesFocusedProduct(product, productId))
          : -1
      }
    })
    .filter(({ index }) => index >= 0)
    .sort((a, b) => a.index - b.index)
    // Two items can resolve to the same card - a duplicated `productId`, or several spotlight-driven
    // slots on one page. Keep the first so its cells are only counted once.
    .filter(({ index }) => {
      if (seen.has(index)) return false
      seen.add(index)
      return true
    })

  if (!matches.length) return new Map()

  // Transition-only: a legacy `<md:col-span-2` card takes 2 cells instead of 1 at `sm`.
  // Cards the eSpot also matches are excluded - they contribute the focused span instead.
  const legacyCells = products.map(
    (product, i) => isLegacyFocused(product) && !seen.has(i) ? 1 : 0
  )

  const brs = Object.keys(ds.breakpoints) as (keyof Responsive)[]

  const assetOverride = ({ assetOverride: asset }: FocusedProductContent) => {
    const urls = brs.reduce((acc, breakpoint) => {
      const media = getValueForBreakpoint(breakpoint, asset)
      // Only a picture can lead the gallery - a video authored here is ignored.
      if (media?.type === 'CMPicture' && media.url) acc[breakpoint] = media.url
      return acc
    }, {} as Partial<Responsive<string>>)

    // `getValueForBreakpoint` only cascades upwards, so an override authored at `lg` alone would
    // leave the smaller breakpoints with no image at all. Every breakpoint gets the nearest asset.
    const [nearest] = Object.values(urls)

    if (!nearest) return undefined

    return brs.reduce(
      (acc, breakpoint) => ({ ...acc, [breakpoint]: urls[breakpoint] || nearest }),
      {} as Responsive<string>
    )
  }

  const placements = matches.map(({ item }) => ({
    style: { 'grid-col': {}, 'grid-row': {} },
    focused: {},
    assetOverride: assetOverride(item)
  } as FocusedProductPlacement))

  brs.forEach((breakpoint) => {
    const columns = getValueForBreakpoint(breakpoint, cols) || 0
    const colSpan = Math.min(FOCUSED_PRODUCT_SPAN, columns)
    const rowSpan = FOCUSED_PRODUCT_SPAN
    // Cells this card adds compared to the normal one it replaces.
    const extra = colSpan * rowSpan - 1
    const legacyTotal = breakpoint === 'sm' ? legacyCells.reduce(sum, 0) : 0

    const extraByBatch: number[] = []
    let totalExtra = 0

    matches.forEach(({ item, index }, i) => {
      const batch = Math.floor(index / productsPerPage)
      const productsBefore = batch * productsPerPage
      const cellsBefore = productsBefore
        + extraByBatch.slice(0, batch).reduce(sum, 0)
        + (breakpoint === 'sm' ? legacyCells.slice(0, productsBefore).reduce(sum, 0) : 0)

      const startRow = (columns ? Math.ceil(cellsBefore / columns) : 0)
        + (getValueForBreakpoint(breakpoint, item.row) || 1)

      const alignment = getValueForBreakpoint(breakpoint, item.rowAlignment) || 'left'
      const startCol = alignment === 'right'
        ? Math.max(columns - colSpan + 1, 1)
        : alignment === 'center'
          ? Math.max(Math.floor((columns - colSpan) / 2) + 1, 1)
          : 1

      // Bounds are global: a row past the end of the grid would strand the card below everything else.
      const totalRows = columns
        ? Math.ceil((products.length + legacyTotal + totalExtra + extra) / columns)
        : 0
      const featured = !!columns && startRow <= totalRows && !item.hideOnBreakpoint?.[breakpoint]

      placements[i].style['grid-col'][breakpoint] = featured ? `${startCol} / span ${colSpan}` : 'auto'
      placements[i].style['grid-row'][breakpoint] = featured ? `${startRow} / span ${rowSpan}` : 'auto'
      placements[i].focused[breakpoint] = featured

      if (featured) {
        totalExtra += extra
        extraByBatch[batch] = (extraByBatch[batch] || 0) + extra
      }
    })
  })

  return new Map(
    matches.flatMap(({ index }, i) =>
      Object.values(placements[i].focused).some(Boolean)
        ? [[index, placements[i]] as const]
        : []
    )
  )
}
